import { useTranslation } from "react-i18next";
import {
  deepCopy,
  formatTime,
  formatTimeRange,
  getByFieldValue,
  getTimezoneFromUser,
  isBlank,
  isEmptyList,
  isNullOrUndefined,
  isSearchQueryMatch,
  logAnalyticsEvent,
  sortByField,
} from "../../utils";
import "./styles.css";
import { tooltipLightStyles } from "../../styleUtils";
import moment from "moment-timezone";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMainContext, useMainDispatchContext } from "../../MainContext";
import {
  useTasksOverviewContext,
  useTasksOverviewDispatchContext,
} from "../../TasksOverviewContext";
import {
  getOperationTypes,
  getUsers,
  getDepartments,
  getTurnaroundsSummarySearch,
} from "../../api";
import { getDepartmentRoster } from "../../tasksOverviewApi";
import {
  ANALYTICS_EVENTS,
  POLLING_INTERVALS,
  SEARCH_QUERY_PLACEHOLDERS,
} from "../../constants";
import { getUserInfo } from "../../userUtils";
import { Fade, Tooltip } from "@mui/material";
import CrewInfo from "../CrewInfo";
import { getTaskDetail, getTaskInfo, isCurrentlyOnShift } from "./utils";
import TaskModal from "./TaskModal";
import CrewDetailModal from "./CrewDetailModal";
import { ReactComponent as Completed } from "../../assets/operations/status-completed.svg";
import { ReactComponent as NotConfirmed } from "../../assets/ban-red.svg";
import LiveRosterFilterButton from "./LiveRosterFilterButton";
import LoadingIndicator from "../LoadingIndicator";
import {
  dedupeUserTasksByOperation,
  getOverlappingTasks,
} from "../../crewShiftUtils";
import { useNavigate, useParams } from "react-router-dom";
import LiveRosterDepartments from "./LiveRosterDepartments";
import { SettingNames, SettingsManager } from "../../SettingsManager";
import LeadBadge from "../LeadBadge";

function LiveRoster() {
  const { t } = useTranslation();

  const dispatch = useMainDispatchContext();
  const navigate = useNavigate();
  const tasksOverviewDispatch = useTasksOverviewDispatchContext();
  const { departmentUuid } = useParams();

  const mainContext = useMainContext();
  const {
    currentUser,
    turnaroundsSummarySearch,
    users,
    operationTypes,
    positions,
    departments,
    isRefreshPaused,
    searchQuery,
  } = mainContext;
  const tasksOverviewContext = useTasksOverviewContext();
  const { departmentRoster } = tasksOverviewContext;

  const airportTimezone = getTimezoneFromUser(currentUser);
  const mNow = moment().tz(airportTimezone);

  const [refreshOverviewRequested, setRefreshOverviewRequested] =
    useState(false);
  const [timelineRanges, setTimelineRanges] = useState({
    mRangeStart: moment(mNow).startOf("hour").add(-2, "hour"),
    mRangeEnd: moment(mNow).startOf("hour").add(10, "hour"),
  });

  const [selectedDepartment, setSelectedDepartment] = useState();
  const [taskInfo, setTaskInfo] = useState(null);
  const [crewInfo, setCrewInfo] = useState(null);
  const [liveRosterFilters, setLiveRosterFilters] = useState({
    showOffShift: false,
    showUnconfirmed: true,
    turnaroundUuids: [],
  });
  const [isInitialized, setIsInitialized] = useState(false);

  // 1hr increments
  const timeBuckets = [];
  const timeInterval = moment(timelineRanges.mRangeStart);
  while (!timeInterval.isAfter(timelineRanges.mRangeEnd)) {
    if (timeInterval.isAfter(timelineRanges.mRangeStart)) {
      timeBuckets.push(moment(timeInterval));
    }
    timeInterval.add(1, "hour");
  }

  useEffect(() => {
    // Interval for refreshing the data within the timeframe
    const intervals = [];

    if (POLLING_INTERVALS.LIVE_ROSTER > 0) {
      const interval = setInterval(() => {
        setRefreshOverviewRequested(true);
      }, POLLING_INTERVALS.LIVE_ROSTER);
      intervals.push(interval);
    }
    return () => {
      for (let i = 0; i < intervals.length; i++) {
        clearInterval(intervals[i]);
      }
    };
  }, []);

  useEffect(() => {
    if (!refreshOverviewRequested) return () => {};
    const mNowUpdated = moment().tz(airportTimezone);
    setTimelineRanges({
      mRangeStart: moment(mNowUpdated).startOf("hour").add(-2, "hour"),
      mRangeEnd: moment(mNowUpdated).startOf("hour").add(10, "hour"),
    });
    setRefreshOverviewRequested(false);
  }, [refreshOverviewRequested, airportTimezone]);

  useEffect(() => {
    if (isEmptyList(departments)) return () => {};
    if (isNullOrUndefined(timelineRanges)) return () => {};

    if (!isBlank(departmentUuid)) {
      // Fetch the turnaround summaries to pair with roster tasks
      getTurnaroundsSummarySearch(dispatch, {
        startTime: timelineRanges.mRangeStart
          .tz(airportTimezone)
          .toDate()
          .toISOString(),
        endTime: timelineRanges.mRangeEnd
          .tz(airportTimezone)
          .toDate()
          .toISOString(),
        includeOpen: true,
        includeClosed: true,
      });
    }
  }, [dispatch, departmentUuid, timelineRanges, departments, airportTimezone]);

  useEffect(() => {
    if (isEmptyList(departments)) return () => {};
    if (isNullOrUndefined(timelineRanges)) return () => {};

    if (!isBlank(departmentUuid)) {
      const department = getByFieldValue(departments, "uuid", departmentUuid);
      if (!isNullOrUndefined(department)) {
        setSelectedDepartment(department);
        getDepartmentRoster(tasksOverviewDispatch, departmentUuid);
        SettingsManager.setSetting(
          SettingNames.CURRENT_DEPARTMENT.name,
          departmentUuid
        );
      } else {
        // Department not found
        setSelectedDepartment(null);
        SettingsManager.removeSetting(SettingNames.CURRENT_DEPARTMENT.name);
        dispatch({
          type: "setGlobalError",
          globalError: t("invalid_department_error"),
        });
        setTimeout(() => {
          navigate(`/live-roster`);
        }, 200);
      }
    }
  }, [
    navigate,
    dispatch,
    t,
    departmentUuid,
    tasksOverviewDispatch,
    timelineRanges,
    departments,
  ]);

  useEffect(() => {
    // Load supporting data here
    if (isNullOrUndefined(timelineRanges)) return () => {};
    if (isRefreshPaused) return () => {};
    getUsers(dispatch);
    getOperationTypes(dispatch);
    getDepartments(dispatch);
  }, [isRefreshPaused, dispatch, timelineRanges]);

  const containerRef = useRef(null);
  const markersRef = useRef(null);
  const gridlinesRef = useRef(null);
  const sidePanelRef = useRef(null);
  const sidePanelHeaderRef = useRef(null);

  function getTimeAsPixels(minutes) {
    const containerEl = gridlinesRef.current;
    const containerWidth = !isNullOrUndefined(containerEl)
      ? containerEl.getBoundingClientRect().width - 2
      : 0;
    const totalWidth = containerWidth;

    const totalDuration = timelineRanges.mRangeEnd.diff(
      timelineRanges.mRangeStart,
      "minutes"
    );
    return minutes * (totalWidth / totalDuration);
  }

  function getTimelineMarkerStyle(mMakerTime) {
    if (isNullOrUndefined(mMakerTime)) return {};
    const durationFromRangeStart = mMakerTime.diff(
      timelineRanges.mRangeStart,
      "minutes"
    );
    const timelineLeft = getTimeAsPixels(durationFromRangeStart);
    return {
      left: `${timelineLeft}px`,
    };
  }

  function getTimelineBarStyle(taskInfo) {
    const mTaskStart = moment(taskInfo.expectedStartTime).tz(airportTimezone);
    const mTaskEnd = moment(taskInfo.expectedEndTime).tz(airportTimezone);
    const isBeforeRangeStart = mTaskStart.isBefore(timelineRanges.mRangeStart);
    const isAfterRangeEnd = mTaskEnd.isAfter(timelineRanges.mRangeEnd);
    const durationFromRangeStart = !isBeforeRangeStart
      ? mTaskStart.diff(timelineRanges.mRangeStart, "minutes")
      : 0;

    // Amount of the task that is trimmed off from the front
    const trimmedDurationFromStart = isBeforeRangeStart
      ? timelineRanges.mRangeStart.diff(mTaskStart, "minutes")
      : 0;
    const durationToRangeEnd = !isNullOrUndefined(mTaskStart)
      ? timelineRanges.mRangeEnd.diff(mTaskStart, "minutes")
      : 0;
    const adjustedDuration = isBeforeRangeStart
      ? taskInfo.duration - trimmedDurationFromStart
      : taskInfo.duration;
    const duration = isAfterRangeEnd
      ? durationToRangeEnd - trimmedDurationFromStart
      : adjustedDuration;
    const timelineWidth = getTimeAsPixels(duration);
    const timelineLeft = getTimeAsPixels(durationFromRangeStart);
    let style = {
      left: `${timelineLeft}px`,
    };
    if (duration > 0) {
      style.width = `${timelineWidth}px`;
    }
    if (isBeforeRangeStart) {
      style.borderTopLeftRadius = "0px";
      style.borderBottomLeftRadius = "0px";
    }
    if (isAfterRangeEnd) {
      style.borderTopRightRadius = "0px";
      style.borderBottomRightRadius = "0px";
    }
    return style;
  }

  // Main function to calculate the gantt timelines
  const [taskInfoList, unassignedTaskInfoList] = useMemo(() => {
    const tasksToRender = [];
    const unassignedTasksToRender = [];
    if (
      !isNullOrUndefined(departmentRoster) &&
      !isEmptyList(users) &&
      !isEmptyList(turnaroundsSummarySearch?.turnarounds) &&
      !isEmptyList(operationTypes)
    ) {
      // Process department roster users and their tasks/shifts
      if (!isEmptyList(departmentRoster?.users)) {
        for (let i = 0; i < departmentRoster.users.length; i++) {
          const rosterUser = departmentRoster.users[i];

          // Process user shifts
          const userShifts = [];
          if (!isEmptyList(rosterUser.shifts)) {
            for (let j = 0; j < rosterUser.shifts.length; j++) {
              const shift = rosterUser.shifts[j];
              const mStart = moment(shift.startTime).startOf("minute");
              const mEnd = moment(shift.endTime).startOf("minute");
              if (!shift.isCalledOff) {
                userShifts.push({
                  expectedStartTime: shift.startTime,
                  expectedEndTime: shift.endTime,
                  duration: mEnd.diff(mStart, "minutes"),
                  departmentName: shift.departmentName,
                  isCalledOff: shift.isCalledOff,
                  notes: shift.notes,
                });
              }
            }
          }

          // Process user tasks
          const userTasks = [];
          if (!isEmptyList(rosterUser.tasks)) {
            for (let j = 0; j < rosterUser.tasks.length; j++) {
              const userTask = rosterUser.tasks[j];
              const taskInfo = getTaskInfo(
                userTask,
                operationTypes,
                turnaroundsSummarySearch?.turnarounds
              );

              if (!isNullOrUndefined(taskInfo)) {
                const isFilteredByTurnaround =
                  !isEmptyList(liveRosterFilters.turnaroundUuids) &&
                  !liveRosterFilters.turnaroundUuids.includes(
                    taskInfo.turnaroundUuid
                  );

                const isFilteredByConfirmed =
                  !liveRosterFilters.showUnconfirmed && !taskInfo.isConfirmed;

                // Check task visible within the timeline window
                const mTaskStart = moment(taskInfo.expectedStartTime).tz(
                  airportTimezone
                );

                if (
                  !isFilteredByTurnaround &&
                  !isFilteredByConfirmed &&
                  mTaskStart.isBefore(timelineRanges.mRangeEnd)
                ) {
                  userTasks.push(taskInfo);
                }
              }
            }
          }

          if (!isEmptyList(userTasks) || !isEmptyList(userShifts)) {
            const user = getByFieldValue(users, "uuid", rosterUser.uuid);
            const userInfo = !isNullOrUndefined(user)
              ? getUserInfo(
                  user,
                  turnaroundsSummarySearch?.turnarounds,
                  positions
                )
              : null;

            // When filtering by turnarounds, do not show users that don't have a task
            const hasTurnaroundFilters = !isEmptyList(
              liveRosterFilters.turnaroundUuids
            );
            const isFilteredByTurnaroundTasksAndNoUserTasks =
              hasTurnaroundFilters && isEmptyList(userTasks);

            const isFilteredBySearch = !isBlank(searchQuery?.searchTerm)
              ? !isSearchQueryMatch(searchQuery?.searchTerm, userInfo?.fullName)
              : false;

            const userOnShift = isCurrentlyOnShift(airportTimezone, userShifts);
            const isFilteredByOffShift =
              !liveRosterFilters.showOffShift && !userOnShift;

            const isFilteredUser =
              isFilteredBySearch ||
              isFilteredByTurnaroundTasksAndNoUserTasks ||
              isFilteredByOffShift;

            if (!isFilteredUser) {
              // Dedupe the tasks by operation (only show one bar per operation regardless of number of assignments)
              const dedupedUserTasks = dedupeUserTasksByOperation(userTasks);
              // Check for overlapping tasks
              const overlappingTasks = getOverlappingTasks(
                dedupedUserTasks,
                airportTimezone
              );

              tasksToRender.push({
                userUuid: rosterUser.uuid,
                userInfo: userInfo,
                tasks: rosterUser.tasks,
                userTasks: dedupedUserTasks,
                userCrewShifts: userShifts,
                hasOverlappingTasks: !isEmptyList(overlappingTasks),
              });
            }
          }
        }
        sortByField(tasksToRender, "userInfo.fullName");
      }

      // Process unassigned tasks from department roster
      if (
        !isEmptyList(departmentRoster?.unassignedTasks) &&
        isBlank(searchQuery?.searchTerm)
      ) {
        for (let i = 0; i < departmentRoster.unassignedTasks.length; i++) {
          const unassignedTask = departmentRoster.unassignedTasks[i];
          const mTaskStart = moment(unassignedTask.expectedStartTime).tz(
            airportTimezone
          );
          const taskInfo = getTaskInfo(
            unassignedTask,
            operationTypes,
            turnaroundsSummarySearch?.turnarounds
          );
          if (
            !isNullOrUndefined(taskInfo) &&
            mTaskStart.isBefore(timelineRanges.mRangeEnd)
          ) {
            const isFiltered =
              !isEmptyList(liveRosterFilters.turnaroundUuids) &&
              !liveRosterFilters.turnaroundUuids.includes(
                taskInfo.turnaroundUuid
              );
            if (!isFiltered) {
              unassignedTasksToRender.push(taskInfo);
            }
          }
        }
        sortByField(unassignedTasksToRender, "expectedStartTime", [
          "operationType.name",
        ]);
      }
      setTimeout(() => {
        setIsInitialized(true);
      }, 1000);
    }
    return [tasksToRender, unassignedTasksToRender];
  }, [
    departmentRoster,
    users,
    turnaroundsSummarySearch,
    operationTypes,
    positions,
    airportTimezone,
    timelineRanges,
    liveRosterFilters,
    searchQuery,
  ]);

  useEffect(() => {
    dispatch({
      type: "setSearchQuery",
      value: {
        searchTerm: "",
        searchPlaceholder: SEARCH_QUERY_PLACEHOLDERS.SEARCH_CREW,
      },
    });
  }, [dispatch]);

  useEffect(() => {
    // Reset scroll
    const scrollEl = containerRef?.current;
    if (!isNullOrUndefined(scrollEl)) {
      scrollEl.scrollTo(0, 0);
    }
  }, [searchQuery?.searchTerm]);

  // Need to correct the vertical position of the label so that it doesn't get covered by header
  const handleContentScroll = useCallback(() => {
    const scrollEl = containerRef.current;
    const markersEl = markersRef.current;
    if (isNullOrUndefined(scrollEl) || isNullOrUndefined(markersEl)) return;

    const scrollTop = scrollEl.scrollTop;
    const scrollLeft = scrollEl.scrollLeft;
    markersEl.style.top = `${scrollTop}px`;

    // Keep sidepanel on left
    const sidePanelEl = sidePanelRef.current;
    const sidePanelHeaderEl = sidePanelHeaderRef.current;

    if (!isNullOrUndefined(sidePanelEl)) {
      sidePanelEl.style.left = `${scrollLeft}px`;
    }
    if (!isNullOrUndefined(sidePanelHeaderEl)) {
      // sidePanelHeaderEl.style.top = `${scrollTop}px`;
      sidePanelHeaderEl.style.left = `${scrollLeft}px`;
    }
  }, []);

  function handleHoverOver(idx) {
    const relatedEls = document.querySelectorAll(`[data-rowindex="${idx}"]`);
    if (!isEmptyList(relatedEls)) {
      for (const element of relatedEls) {
        element.classList.add("hovered");
      }
    }
  }
  function handleHoverOut(idx) {
    const relatedEls = document.querySelectorAll(`[data-rowindex="${idx}"]`);
    if (!isEmptyList(relatedEls)) {
      for (const element of relatedEls) {
        element.classList.remove("hovered");
      }
    }
  }

  // NOTE: These are fixed size for now and match with the values in CSS
  // Maybe consider user-settable sizes or based on viewport size
  const colsize = "160px";
  const rowsize = "45px";
  const columnStyle = {
    gridTemplateColumns: `repeat(${timeBuckets.length}, ${colsize})`,
  };

  // Calculate total rows after taskInfoList is created
  const totalRows =
    (unassignedTaskInfoList?.length || 0) + (taskInfoList?.length || 0);
  const rowStyle = {
    gridTemplateRows: `repeat(${totalRows}, ${rowsize})`,
  };

  // Styles to position the markers
  const nowMarkerStyle = getTimelineMarkerStyle(mNow);

  // Show modal for editing assignments
  function handleShowAssignmentModal(taskInfo) {
    dispatch({
      type: "setIsRefreshPaused",
      value: true,
    });
    setTaskInfo(taskInfo);
    if (!isNullOrUndefined(taskInfo.unassignedTask)) {
      logAnalyticsEvent(dispatch, ANALYTICS_EVENTS.LIVE_ROSTER_UNASSIGNED_VIEW);
    } else {
      logAnalyticsEvent(dispatch, ANALYTICS_EVENTS.LIVE_ROSTER_TASK_VIEW);
    }
  }

  // Show modal for crew details
  function handleShowCrewModal(taskInfo) {
    dispatch({
      type: "setIsRefreshPaused",
      value: true,
    });
    // taskInfo already includes the userInfo (taskInfo.userInfo)
    setCrewInfo(taskInfo);
    logAnalyticsEvent(dispatch, ANALYTICS_EVENTS.LIVE_ROSTER_VIEW_CREW);
  }

  function handleNowOver(markerClass) {
    const markerEls = Array.from(document.getElementsByClassName(markerClass));
    if (!isEmptyList(markerEls)) {
      markerEls.forEach((el) => {
        el.classList.add("hovered");
      });
    }
  }
  function handleNowOut(markerClass) {
    const markerEls = Array.from(document.getElementsByClassName(markerClass));
    if (!isEmptyList(markerEls)) {
      markerEls.forEach((el) => {
        el.classList.remove("hovered");
      });
    }
  }

  function handleUnassignedBannerMouseOver() {
    document.querySelector(".tasks-overview").classList.add("highlight");
  }
  function handleUnassignedBannerMouseOut() {
    document.querySelector(".tasks-overview").classList.remove("highlight");
  }
  function handleUnassignedBannerClick() {
    // scroll down
    const unassignedEl = document.querySelector(
      ".tasks-overview .tasks-overview-gridlines .unassigned"
    );
    const containerEl = document.querySelector(".tasks-overview-container");
    containerEl.scrollTo({
      left: 0,
      top: unassignedEl.offsetTop,
      behavior: "smooth",
    });
  }

  const showEmptyState =
    isEmptyList(taskInfoList) && isEmptyList(unassignedTaskInfoList);

  return (
    <div className="tasks-overview">
      <div className="tasks-overview-header">
        <div className="titles">
          <h3>
            {t("live_roster")} &rsaquo;{" "}
            {!isNullOrUndefined(selectedDepartment)
              ? selectedDepartment.name
              : t("departments")}
          </h3>
          {!isNullOrUndefined(selectedDepartment) && (
            <div className="subtitle">{t("view_and_manage_tasks")}</div>
          )}
        </div>
        {!isNullOrUndefined(selectedDepartment) && (
          <div className="actions">
            <div>
              {!isEmptyList(unassignedTaskInfoList) && (
                <div
                  className="unassigned-banner"
                  onClick={handleUnassignedBannerClick}
                  onMouseOver={handleUnassignedBannerMouseOver}
                  onMouseOut={handleUnassignedBannerMouseOut}
                >
                  <div>
                    <div>{`${unassignedTaskInfoList.length}`}</div>
                    <div>
                      <span>{t("unassigned")}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div>
              <button
                className="secondary"
                onClick={() => {
                  SettingsManager.removeSetting(
                    SettingNames.CURRENT_DEPARTMENT.name
                  );
                  setSelectedDepartment(null);
                  navigate(`/live-roster`);
                  logAnalyticsEvent(
                    dispatch,
                    ANALYTICS_EVENTS.LIVE_ROSTER_CHANGE_DEPARTMENT
                  );
                }}
              >
                {t("change_department")}
              </button>
            </div>
            <div>
              <LiveRosterFilterButton
                rosterInfo={departmentRoster}
                turnarounds={turnaroundsSummarySearch?.turnarounds}
                liveRosterFilters={liveRosterFilters}
                onChange={(turnaroundUuid) => {
                  setLiveRosterFilters((prev) => {
                    const updatedFilters = deepCopy(prev);
                    const idx =
                      updatedFilters.turnaroundUuids.indexOf(turnaroundUuid);
                    if (idx > -1) {
                      updatedFilters.turnaroundUuids.splice(idx, 1);
                    } else {
                      updatedFilters.turnaroundUuids.push(turnaroundUuid);
                    }
                    return updatedFilters;
                  });
                  // Reset scroll
                  const scrollEl = containerRef?.current;
                  if (!isNullOrUndefined(scrollEl)) {
                    scrollEl.scrollTo(0, 0);
                  }
                  logAnalyticsEvent(
                    dispatch,
                    ANALYTICS_EVENTS.LIVE_ROSTER_FILTER
                  );
                }}
                onClear={() => {
                  setLiveRosterFilters((prev) => {
                    const updatedFilters = deepCopy(prev);
                    updatedFilters.turnaroundUuids = [];
                    updatedFilters.showUnconfirmed = true;
                    updatedFilters.showOffShift = true;
                    return updatedFilters;
                  });
                }}
                toggleShowConfirmed={() => {
                  setLiveRosterFilters((prev) => {
                    const updatedFilters = deepCopy(prev);
                    updatedFilters.showUnconfirmed =
                      !updatedFilters.showUnconfirmed;
                    return updatedFilters;
                  });
                  logAnalyticsEvent(
                    dispatch,
                    ANALYTICS_EVENTS.LIVE_ROSTER_FILTER
                  );
                }}
                toggleShowOffShift={() => {
                  setLiveRosterFilters((prev) => {
                    const updatedFilters = deepCopy(prev);
                    updatedFilters.showOffShift = !updatedFilters.showOffShift;
                    return updatedFilters;
                  });
                  logAnalyticsEvent(
                    dispatch,
                    ANALYTICS_EVENTS.LIVE_ROSTER_FILTER
                  );
                }}
              />
            </div>
          </div>
        )}
      </div>
      {isNullOrUndefined(selectedDepartment) && (
        <div className="tasks-overview-body">
          <LiveRosterDepartments departments={departments} />
        </div>
      )}
      {!isNullOrUndefined(selectedDepartment) && (
        <div className="tasks-overview-body">
          {showEmptyState && (
            <div>
              <div className="empty-state">
                {isInitialized && t("no_results")}
                {!isInitialized && <LoadingIndicator />}
              </div>
            </div>
          )}
          <div
            className={`tasks-overview-container ${
              showEmptyState ? " not-visible" : ""
            }`}
            ref={containerRef}
            onScroll={handleContentScroll}
          >
            <div className="tasks-overview-header-row">
              <div
                className="side-panel-header cell-label"
                ref={sidePanelHeaderRef}
              >
                <div>{t("name")}</div>
              </div>
              <div className="time-marker-labels" style={columnStyle}>
                {timeBuckets &&
                  timeBuckets.map((i, idx) => {
                    const isInNowRange =
                      mNow.isAfter(moment(i).add(-15, "minutes")) &&
                      mNow.isBefore(moment(i).add(15, "minutes"));
                    return (
                      <div className="cell" key={`header-${idx}`}>
                        <div>
                          {!isInNowRange &&
                            idx + 1 < timeBuckets.length &&
                            formatTime(i, airportTimezone)}
                        </div>
                      </div>
                    );
                  })}
              </div>
              <div
                className="tasks-overview-timeline-markers"
                style={{ top: 0 }}
              >
                <div
                  className="timeline-marker timeline-marker-now"
                  style={nowMarkerStyle}
                  onMouseOver={() => {
                    handleNowOver("timeline-marker-now");
                  }}
                  onMouseOut={() => {
                    handleNowOut("timeline-marker-now");
                  }}
                >
                  <div>
                    <div className="timeline-marker-label-anchor">
                      <div className="timeline-marker-label">
                        <div>
                          <div className="timeline-marker-label-title">
                            {t("now")}
                          </div>
                          <div className="timeline-marker-label-value">
                            {formatTime(mNow, airportTimezone)}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="timeline-marker-line"></div>
                  </div>
                </div>
              </div>
            </div>
            <div className="tasks-overview-content">
              <div
                className="tasks-overview-sidepanel"
                style={rowStyle}
                ref={sidePanelRef}
              >
                {!isEmptyList(taskInfoList) &&
                  taskInfoList.map((taskInfo, idx) => (
                    <div
                      className="cell-label clickable"
                      key={`sidepanel-${idx}`}
                      data-rowindex={idx}
                      onMouseOver={() => {
                        handleHoverOver(idx);
                      }}
                      onMouseOut={() => {
                        handleHoverOut(idx);
                      }}
                      onClick={() => {
                        handleShowCrewModal(taskInfo);
                      }}
                    >
                      <div
                        className={`tasks-overview-crew-info${
                          taskInfo.hasOverlappingTasks
                            ? " highlight-warning"
                            : ""
                        }`}
                      >
                        <Tooltip
                          title={
                            <CrewCertsTooltip
                              userInfo={taskInfo.userInfo}
                              hasOverlappingTasks={taskInfo.hasOverlappingTasks}
                            />
                          }
                          placement="left"
                          componentsProps={tooltipLightStyles}
                          TransitionComponent={Fade}
                          TransitionProps={{ timeout: 500 }}
                          enterDelay={500}
                          arrow
                        >
                          <div>
                            <CrewInfo
                              user={taskInfo?.userInfo?.user}
                              mapStatus={taskInfo?.userInfo?.mapStatus}
                            />
                          </div>
                        </Tooltip>
                      </div>
                    </div>
                  ))}
                {!isEmptyList(unassignedTaskInfoList) &&
                  unassignedTaskInfoList.map((unassignedTask, idx) => (
                    <div
                      className="cell-label unassigned"
                      key={`sidepanel-unassigned-${idx}`}
                      data-rowindex={`unassigned-${idx}`}
                      onMouseOver={() => {
                        handleHoverOver(`unassigned-${idx}`);
                      }}
                      onMouseOut={() => {
                        handleHoverOut(`unassigned-${idx}`);
                      }}
                    >
                      <div>
                        <CrewInfo
                          user={{
                            firstName: t("unassigned"),
                            lasName: "",
                          }}
                        />
                      </div>
                    </div>
                  ))}
              </div>
              <div className="tasks-overview-mainpanel">
                <div
                  className="tasks-overview-gridlines"
                  style={rowStyle}
                  ref={gridlinesRef}
                >
                  {taskInfoList &&
                    taskInfoList.map((taskInfo, idx) => (
                      <div
                        key={`grid-${idx}`}
                        style={columnStyle}
                        data-rowindex={idx}
                        onMouseOver={() => {
                          handleHoverOver(idx);
                        }}
                        onMouseOut={() => {
                          handleHoverOut(idx);
                        }}
                      >
                        {timeBuckets &&
                          timeBuckets.map((i, subIdx) => (
                            <div
                              className="gridcell"
                              key={`grid-${idx}-${subIdx}`}
                            >
                              <div data-time-bucket={formatTime(i)}></div>
                            </div>
                          ))}
                      </div>
                    ))}
                  {!isEmptyList(unassignedTaskInfoList) &&
                    unassignedTaskInfoList.map((unassignedTask, idx) => (
                      <div
                        key={`grid-${idx}`}
                        style={columnStyle}
                        data-rowindex={`unassigned-${idx}`}
                        onMouseOver={() => {
                          handleHoverOver(`unassigned-${idx}`);
                        }}
                        onMouseOut={() => {
                          handleHoverOut(`unassigned-${idx}`);
                        }}
                        className="unassigned"
                      >
                        {timeBuckets &&
                          timeBuckets.map((i, subIdx) => (
                            <div
                              className="gridcell"
                              key={`grid-${idx}-${subIdx}`}
                            >
                              <div data-time-bucket={formatTime(i)}></div>
                            </div>
                          ))}
                      </div>
                    ))}
                </div>

                <div className="tasks-overview-timelinebars" style={rowStyle}>
                  {!isEmptyList(taskInfoList) &&
                    taskInfoList.map((taskInfo, idx) => (
                      <div key={`timelinebar-${idx}`}>
                        {/** Crew shift timeline bars **/}
                        {taskInfo?.userCrewShifts &&
                          taskInfo?.userCrewShifts.map((userTask, subIdx) => {
                            const timelineStyle = getTimelineBarStyle(userTask);
                            return (
                              <Tooltip
                                key={`timelinebar-crewshift-${idx}-${subIdx}`}
                                title={
                                  <CrewShiftTooltip
                                    crewShift={userTask}
                                    timezone={airportTimezone}
                                  />
                                }
                                followCursor={true}
                                componentsProps={tooltipLightStyles}
                                TransitionComponent={Fade}
                                TransitionProps={{ timeout: 500 }}
                                enterDelay={500}
                                arrow
                              >
                                <div
                                  className="tasks-overview-timelinebar crewshift"
                                  style={timelineStyle}
                                >
                                  <div>
                                    <div className="tasks-overview-timelinebar-value"></div>
                                  </div>
                                </div>
                              </Tooltip>
                            );
                          })}
                        {/** Task timeline bars **/}
                        {taskInfo?.userTasks &&
                          taskInfo?.userTasks.map((userTask, subIdx) => {
                            const timelineStyle = getTimelineBarStyle(userTask);
                            const isLeadTask =
                              userTask?.operationType?.isLeadOperation;
                            return (
                              <Tooltip
                                key={`timelinebar-task-${idx}-${subIdx}`}
                                title={
                                  <TaskTooltip
                                    assignedUser={taskInfo?.userInfo}
                                    userTask={userTask}
                                    timezone={airportTimezone}
                                    users={users}
                                    showDetails={true}
                                  />
                                }
                                placement="top"
                                componentsProps={tooltipLightStyles}
                                TransitionComponent={Fade}
                                TransitionProps={{ timeout: 500 }}
                                arrow
                              >
                                <div
                                  className={`tasks-overview-timelinebar${
                                    userTask.duration === 0 ? " point" : ""
                                  }${userTask.completed ? " completed" : ""}${
                                    userTask.started && !userTask.completed
                                      ? " started pulseBg"
                                      : ""
                                  }${
                                    !userTask.isConfirmed
                                      ? " not-confirmed"
                                      : ""
                                  }${
                                    userTask.isOverlapping ? " overlapping" : ""
                                  }`}
                                  style={timelineStyle}
                                  onClick={() => {
                                    handleShowAssignmentModal({
                                      userUuid: taskInfo.userUuid,
                                      userTask: userTask,
                                    });
                                  }}
                                >
                                  <div>
                                    <div className="tasks-overview-timelinebar-value">
                                      <div>
                                        <TaskStatusIndicator
                                          userTask={userTask}
                                        />
                                      </div>
                                      <div>
                                        {userTask?.turnaroundInfo
                                          ?.combinedFlightName ||
                                          userTask?.turnaroundUuid}
                                        : {userTask?.operationType?.name}
                                        {isLeadTask && <LeadBadge />}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </Tooltip>
                            );
                          })}
                      </div>
                    ))}
                  {!isEmptyList(unassignedTaskInfoList) &&
                    unassignedTaskInfoList.map((unassignedTask, idx) => {
                      const timelineStyle = getTimelineBarStyle(unassignedTask);
                      const isLeadTask =
                        unassignedTask?.operationType?.isLeadOperation;
                      return (
                        <div key={`unassigned-${idx}`}>
                          <Tooltip
                            title={
                              <TaskTooltip
                                userTask={unassignedTask}
                                showDetails={true}
                                timezone={airportTimezone}
                              />
                            }
                            placement="top"
                            componentsProps={tooltipLightStyles}
                            TransitionComponent={Fade}
                            TransitionProps={{ timeout: 500 }}
                            arrow
                          >
                            <div
                              className={`tasks-overview-timelinebar unassigned${
                                unassignedTask.duration === 0 ? " point" : ""
                              }${unassignedTask.completed ? " completed" : ""}${
                                unassignedTask.started &&
                                !unassignedTask.completed
                                  ? " started"
                                  : ""
                              }`}
                              style={timelineStyle}
                              onClick={() => {
                                handleShowAssignmentModal({
                                  unassignedTask: unassignedTask,
                                });
                              }}
                            >
                              <div>
                                <div className="tasks-overview-timelinebar-value">
                                  {unassignedTask.completed && (
                                    <div>
                                      <Completed />
                                    </div>
                                  )}
                                  {unassignedTask.started &&
                                    !unassignedTask.completed && (
                                      <div>
                                        <div className="pulse fadeInLoop"></div>
                                      </div>
                                    )}
                                  <div>
                                    {unassignedTask?.turnaroundInfo
                                      ?.combinedFlightName ||
                                      unassignedTask?.turnaroundUuid}
                                    : {unassignedTask?.operationType?.name}
                                    {isLeadTask && <LeadBadge />}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </Tooltip>
                        </div>
                      );
                    })}
                </div>
              </div>
            </div>
            <div
              className="tasks-overview-timeline-markers"
              style={{ top: 0 }}
              ref={markersRef}
            >
              <div
                className="timeline-marker timeline-marker-now"
                style={nowMarkerStyle}
                onMouseOver={() => {
                  handleNowOver("timeline-marker-now");
                }}
                onMouseOut={() => {
                  handleNowOut("timeline-marker-now");
                }}
              >
                <div>
                  <div className="timeline-marker-label-anchor">
                    <div className="timeline-marker-label temp-hidden">
                      <div>
                        <div className="timeline-marker-label-title">Now</div>
                        <div className="timeline-marker-label-value">
                          {formatTime(mNow, airportTimezone)}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="timeline-marker-line"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {!isNullOrUndefined(taskInfo) && (
        <TaskModal
          taskInfo={taskInfo}
          rosterInfo={departmentRoster}
          onClose={() => {
            dispatch({
              type: "setIsRefreshPaused",
              value: false,
            });
            setTaskInfo(null);
          }}
          timezone={airportTimezone}
        />
      )}
      {!isNullOrUndefined(crewInfo) && (
        <CrewDetailModal
          crewInfo={crewInfo}
          onClose={() => {
            dispatch({
              type: "setIsRefreshPaused",
              value: false,
            });
            setCrewInfo(null);
          }}
          timezone={airportTimezone}
          operationTypes={operationTypes}
          turnarounds={turnaroundsSummarySearch?.turnarounds}
          users={users}
        />
      )}
    </div>
  );
}
function CrewCertsTooltip(props) {
  const { t } = useTranslation();
  const { userInfo, hasOverlappingTasks } = props;
  return (
    <div className="tooltip-content">
      <div className="tooltip-content-heading">{`${t("certifications")} (${
        userInfo.certList.length
      })`}</div>
      <div className="tooltip-timestamps">
        {userInfo.certList.map((certName, idx) => (
          <div key={idx}>{certName}</div>
        ))}
      </div>
      {hasOverlappingTasks && (
        <div className="warning">{t("live_roster_overlapping_tasks")}</div>
      )}
    </div>
  );
}
function CrewShiftTooltip(props) {
  const { t } = useTranslation();
  const { crewShift, timezone } = props;
  return (
    <div className="tooltip-content">
      <div className="tooltip-content-heading">{t("on_shift")}</div>
      <div className="tooltip-timestamps">
        {formatTimeRange(
          crewShift.expectedStartTime,
          crewShift.expectedEndTime,
          timezone
        )}
      </div>
    </div>
  );
}

function TaskTooltip(props) {
  const { userTask, timezone, users, showDetails, assignedUser } = props;
  const taskDetail = getTaskDetail(userTask, users, assignedUser);
  const isLeadTask = userTask?.operationType?.isLeadOperation;

  return (
    <div className="tooltip-content">
      <div className="tooltip-content-heading">
        <div className="tooltip-content-heading-label">
          {userTask.turnaroundInfo.combinedFlightName}
        </div>
        <div className="tooltip-content-heading-sublabel">
          {userTask.operationType.name}
          {isLeadTask && <LeadBadge />}
        </div>
      </div>
      <div className="tooltip-content-timestamps">
        {showDetails && !isNullOrUndefined(taskDetail) && (
          <span
            className={`${
              userTask.completed
                ? "completed"
                : userTask.started
                ? "in-progress"
                : ""
            }`}
          >
            {taskDetail}
          </span>
        )}
      </div>
      <div className="tooltip-content-timeranges">
        <span>
          {formatTimeRange(
            userTask.expectedStartTime,
            userTask.expectedEndTime,
            timezone
          )}
        </span>
      </div>
    </div>
  );
}

function TaskStatusIndicator(props) {
  const { userTask } = props;
  const isCompleted = userTask.completed;
  const isInProgress = userTask.started && !userTask.completed;

  const isConfirmed = userTask.isConfirmed;
  if (isInProgress) {
    return <div className="pulse"></div>;
  } else if (isCompleted) {
    return <Completed />;
  } else if (!isConfirmed) {
    return <NotConfirmed />;
  }
  return null;
}

export default LiveRoster;
